((root, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaBuildCanvas = api;
})(typeof globalThis !== 'undefined' ? globalThis : window, () => {
  'use strict';

  const VERSION = 'visual-build-canvas-v1';
  const BUILD_DNA_VERSION = 'build-dna-v3';
  const MAX_PLACEMENTS = 60;
  const ZOOM_LEVELS = Object.freeze([0.75, 1, 1.25, 1.5]);
  const REGION_ROWS = Object.freeze(['upper', 'middle', 'lower']);
  const REGION_COLUMNS = Object.freeze(['left', 'center', 'right']);
  const REGIONS = Object.freeze(REGION_ROWS.flatMap((row) =>
    REGION_COLUMNS.map((column) => `${row}_${column}`)
  ));
  const LAYERS = Object.freeze([
    Object.freeze({ id: 'background', label: 'Background', status: 'active' }),
    Object.freeze({ id: 'structure', label: 'Structure', status: 'active' }),
    Object.freeze({ id: 'climbing', label: 'Climbing', status: 'active' }),
    Object.freeze({ id: 'hides', label: 'Hides', status: 'active' }),
    Object.freeze({ id: 'display_pieces', label: 'Display Pieces', status: 'active' }),
    Object.freeze({ id: 'accent_pieces', label: 'Accent Pieces', status: 'active' }),
    Object.freeze({ id: 'plants', label: 'Plants', status: 'future' }),
    Object.freeze({ id: 'substrate', label: 'Substrate', status: 'future' }),
    Object.freeze({ id: 'hardscape', label: 'Hardscape', status: 'future' }),
    Object.freeze({ id: 'lighting', label: 'Lighting', status: 'future' }),
    Object.freeze({ id: 'water', label: 'Water', status: 'future' })
  ]);
  const ACTIVE_LAYER_IDS = Object.freeze(LAYERS.filter((layer) => layer.status === 'active').map((layer) => layer.id));
  const FUNCTION_LAYERS = Object.freeze({
    background_coverage: 'background',
    vertical_structure: 'structure',
    climbing_feature: 'climbing',
    orchid_mount: 'climbing',
    primary_hide: 'hides',
    secondary_hide: 'hides',
    display_piece: 'display_pieces',
    accent_piece: 'accent_pieces'
  });
  const FAMILY_LAYERS = Object.freeze({
    background_piece: 'background',
    cork_flat: 'background',
    branch: 'climbing',
    cork_round: 'hides',
    half_round: 'hides',
    statement_piece: 'display_pieces'
  });
  const LAYER_REGIONS = Object.freeze({
    background: ['middle_center', 'upper_center', 'lower_center', 'middle_left', 'middle_right'],
    structure: ['lower_center', 'middle_center', 'upper_center', 'lower_left', 'lower_right'],
    climbing: ['upper_left', 'upper_right', 'middle_center', 'middle_left', 'middle_right'],
    hides: ['lower_left', 'lower_right', 'lower_center', 'middle_left', 'middle_right'],
    display_pieces: ['middle_center', 'lower_center', 'middle_left', 'middle_right'],
    accent_pieces: ['lower_right', 'middle_right', 'lower_left', 'middle_left', 'upper_center']
  });

  const unique = (values) => [...new Set((values || []).filter(Boolean))];
  const toVariantId = (value) => String(value ?? '').trim();
  const humanize = (value) => String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
  const clampZoom = (value) => {
    const zoom = Number(value);
    return ZOOM_LEVELS.includes(zoom) ? zoom : 1;
  };
  const validRegion = (value) => REGIONS.includes(String(value)) ? String(value) : 'middle_center';
  const validLayer = (value) => ACTIVE_LAYER_IDS.includes(String(value)) ? String(value) : 'accent_pieces';

  const createState = () => ({
    version: VERSION,
    zoom: 1,
    activeLayer: 'all',
    selectedVariantId: null,
    placements: {},
    futureLayoutPreferences: {
      status: 'architecture_only',
      storesCartPayload: false
    }
  });

  const sanitizeState = (input = {}) => {
    const placements = {};
    Object.entries(input?.placements || {}).slice(0, MAX_PLACEMENTS).forEach(([rawVariantId, placement]) => {
      const variantId = toVariantId(rawVariantId);
      if (!variantId) return;
      placements[variantId] = {
        region: validRegion(placement?.region),
        layer: validLayer(placement?.layer),
        highlighted: placement?.highlighted === true
      };
    });
    const selectedVariantId = toVariantId(input?.selectedVariantId);
    return {
      ...createState(),
      zoom: clampZoom(input?.zoom),
      activeLayer: input?.activeLayer === 'all' || ACTIVE_LAYER_IDS.includes(input?.activeLayer)
        ? input.activeLayer
        : 'all',
      selectedVariantId: selectedVariantId && placements[selectedVariantId] ? selectedVariantId : null,
      placements
    };
  };

  const acceptedDecision = (decisions, variantId) => [...(decisions || [])].reverse().find((decision) =>
    decision?.action === 'keep' && toVariantId(decision.variantId) === toVariantId(variantId)
  );

  const layerFor = (piece = {}, decisions = []) => {
    const decision = acceptedDecision(decisions, piece.variantId);
    return FUNCTION_LAYERS[decision?.functionId]
      || FUNCTION_LAYERS[piece.functionId]
      || FAMILY_LAYERS[piece.family]
      || 'accent_pieces';
  };

  const defaultRegion = (layer, index = 0) => {
    const regions = LAYER_REGIONS[validLayer(layer)] || REGIONS;
    return regions[Math.abs(Number(index) || 0) % regions.length];
  };

  const reconcile = (rawState, pieces = [], decisions = []) => {
    const current = sanitizeState(rawState);
    const placements = {};
    pieces.slice(0, MAX_PLACEMENTS).forEach((piece, index) => {
      const variantId = toVariantId(piece?.variantId);
      if (!variantId) return;
      const layer = layerFor(piece, decisions);
      placements[variantId] = current.placements[variantId]
        ? { ...current.placements[variantId] }
        : { layer, region: defaultRegion(layer, index), highlighted: false };
    });
    return sanitizeState({
      ...current,
      placements,
      selectedVariantId: placements[current.selectedVariantId] ? current.selectedVariantId : null
    });
  };

  const updatePlacement = (rawState, variantIdValue, patch = {}) => {
    const state = sanitizeState(rawState);
    const variantId = toVariantId(variantIdValue);
    if (!state.placements[variantId]) return state;
    return sanitizeState({
      ...state,
      selectedVariantId: variantId,
      placements: {
        ...state.placements,
        [variantId]: {
          ...state.placements[variantId],
          ...(patch.region ? { region: validRegion(patch.region) } : {}),
          ...(patch.layer ? { layer: validLayer(patch.layer) } : {}),
          ...(typeof patch.highlighted === 'boolean' ? { highlighted: patch.highlighted } : {})
        }
      }
    });
  };

  const moveRegion = (regionValue, direction) => {
    const [rowValue, columnValue] = validRegion(regionValue).split('_');
    let row = REGION_ROWS.indexOf(rowValue);
    let column = REGION_COLUMNS.indexOf(columnValue);
    if (direction === 'up') row = Math.max(0, row - 1);
    if (direction === 'down') row = Math.min(REGION_ROWS.length - 1, row + 1);
    if (direction === 'left') column = Math.max(0, column - 1);
    if (direction === 'right') column = Math.min(REGION_COLUMNS.length - 1, column + 1);
    return `${REGION_ROWS[row]}_${REGION_COLUMNS[column]}`;
  };

  const placementCoordinates = (placement = {}, index = 0) => {
    const region = validRegion(placement.region);
    const [row, column] = region.split('_');
    const baseX = { left: 18, center: 50, right: 82 }[column];
    const baseY = { upper: 19, middle: 50, lower: 80 }[row];
    const offsetX = ((index * 17) % 11) - 5;
    const offsetY = ((index * 13) % 9) - 4;
    return {
      x: Math.max(8, Math.min(92, baseX + offsetX)),
      y: Math.max(9, Math.min(91, baseY + offsetY)),
      rotation: ((index * 19) % 31) - 15
    };
  };

  const composition = (rawState, pieces = [], decisions = []) => {
    const state = reconcile(rawState, pieces, decisions);
    const currentIds = new Set(pieces.map((piece) => toVariantId(piece.variantId)));
    const chosenFunctions = unique((decisions || [])
      .filter((decision) => decision?.action === 'keep' && currentIds.has(toVariantId(decision.variantId)))
      .map((decision) => decision.functionId));
    const chosenLayers = unique(Object.values(state.placements).map((placement) => placement.layer));
    const layerCounts = Object.fromEntries(ACTIVE_LAYER_IDS.map((layer) => [
      layer,
      Object.values(state.placements).filter((placement) => placement.layer === layer).length
    ]));
    const regionCounts = Object.fromEntries(REGIONS.map((region) => [
      region,
      Object.values(state.placements).filter((placement) => placement.region === region).length
    ]));
    const layerPresent = (layer) => layerCounts[layer] > 0;
    const health = [
      {
        id: 'coverage',
        label: 'Coverage',
        status: layerPresent('background') ? 'Background improved' : 'Background is still open',
        evidence: layerPresent('background')
          ? `${layerCounts.background} selected ${layerCounts.background === 1 ? 'piece is' : 'pieces are'} planned on the Background layer.`
          : 'No selected Shopify cart piece is planned on the Background layer.'
      },
      {
        id: 'vertical_structure',
        label: 'Vertical structure',
        status: layerPresent('structure') || layerPresent('climbing')
          ? 'Vertical structure increased'
          : 'Vertical structure is still open',
        evidence: `${layerCounts.structure + layerCounts.climbing} selected ${layerCounts.structure + layerCounts.climbing === 1 ? 'piece is' : 'pieces are'} planned for Structure or Climbing.`
      },
      {
        id: 'hides',
        label: 'Hides',
        status: layerPresent('hides') ? 'A hide is represented' : 'A hide is still open',
        evidence: `${layerCounts.hides} selected ${layerCounts.hides === 1 ? 'piece is' : 'pieces are'} planned on the Hides layer.`
      },
      {
        id: 'visual_focus',
        label: 'Visual focus',
        status: layerPresent('display_pieces') ? 'A focal point is represented' : 'A focal point is optional',
        evidence: `${layerCounts.display_pieces} selected ${layerCounts.display_pieces === 1 ? 'piece is' : 'pieces are'} planned on the Display Pieces layer.`
      }
    ];
    const timelineDefinitions = [
      ['foundation', 'Foundation', ['structure', 'display_pieces']],
      ['primary_hide', 'Primary Hide', ['hides']],
      ['climbing', 'Climbing', ['climbing']],
      ['background', 'Background', ['background']],
      ['accent', 'Accent', ['accent_pieces']]
    ];
    const timeline = timelineDefinitions.map(([id, label, layers]) => ({
      id,
      label,
      complete: layers.some(layerPresent),
      status: 'pending'
    }));
    const firstPending = timeline.findIndex((entry) => !entry.complete);
    timeline.forEach((entry, index) => {
      entry.status = entry.complete ? 'complete' : index === firstPending ? 'current' : 'pending';
    });
    return {
      version: VERSION,
      pieceCount: pieces.length,
      pieceOrder: pieces.map((piece) => toVariantId(piece.variantId)),
      chosenFunctions,
      chosenLayers,
      layerCounts,
      regionCounts,
      health,
      timeline,
      limitation: 'Visual placement is relative planning only. It is not CAD, packing, compatibility, or measured coverage.'
    };
  };

  const expandBuildDna = (existing = {}, rawState, pieces = [], decisions = []) => {
    const state = reconcile(rawState, pieces, decisions);
    const summary = composition(state, pieces, decisions);
    return {
      ...existing,
      version: BUILD_DNA_VERSION,
      piece_order: pieces.map((piece, index) => ({
        position: index + 1,
        variant_id: toVariantId(piece.variantId),
        piece_id: piece.pieceId || null,
        source: 'shopify_cart'
      })),
      placements: pieces.map((piece) => {
        const placement = state.placements[toVariantId(piece.variantId)];
        return {
          variant_id: toVariantId(piece.variantId),
          piece_id: piece.pieceId || null,
          region: placement?.region || null,
          layer: placement?.layer || null,
          source: 'local_build_canvas'
        };
      }),
      build_composition: {
        piece_count: summary.pieceCount,
        layer_counts: summary.layerCounts,
        region_counts: summary.regionCounts,
        not_to_scale: true
      },
      chosen_functions: summary.chosenFunctions,
      chosen_layers: summary.chosenLayers,
      future_layout_preferences: {
        status: 'architecture_only',
        supported_layers: LAYERS.filter((layer) => layer.status === 'future').map((layer) => layer.id),
        authoritative_cart_copy: false
      },
      authority: {
        ...(existing.authority || {}),
        cart: 'shopify',
        canvas_membership: 'shopify_cart',
        placement: 'local_build_canvas',
        spatial_accuracy: 'not_to_scale'
      }
    };
  };

  return Object.freeze({
    VERSION,
    BUILD_DNA_VERSION,
    MAX_PLACEMENTS,
    ZOOM_LEVELS,
    REGION_ROWS,
    REGION_COLUMNS,
    REGIONS,
    LAYERS,
    ACTIVE_LAYER_IDS,
    FUNCTION_LAYERS,
    createState,
    sanitizeState,
    layerFor,
    defaultRegion,
    reconcile,
    updatePlacement,
    moveRegion,
    placementCoordinates,
    composition,
    expandBuildDna,
    humanize
  });
});
